import { useEffect, useState } from "react";
import "./WorkTimePlanning.css";
import axios from "axios";
import { FormControlLabel, IconButton, Switch } from "@mui/material";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CustomSnackbar from "../../../sharedComponents/snackbar/CustomSnackbar";
import GetWeekNumber from "../../../sharedComponents/GetWeekNumber";
import PrimaryButton from "../../../sharedComponents/primaryButton/PrimaryButton";
import CancelButton from "../../../sharedComponents/cancelButton/CancelButton";
import PlanningTable from "./planningTable/PlanningTable";
import PlanningScheduler from "./planningScheduler/PlanningScheduler";
import Logout from "../../../sharedComponents/Logout";
import FilterArea from "../../../sharedComponents/filterArea/FilterArea";

const DATE_OPTIONS = { day: '2-digit', month: '2-digit', year: 'numeric' };

function WorkTimePlanning() {

    const [isLoading, setIsLoading] = useState(true);
    const [currentDate, setCurrentDate] = useState(getMonday(sessionStorage.getItem("currentDate") ? sessionStorage.getItem("currentDate") : new Date()));
    const [localChanges, setLocalChanges] = useState({});
    const [snackbarConfig, setSnackbarConfig] = useState({ open: false, message: "", variant: "" });
    const [enableTimeline, setEnableTimeline] = useState(false);
    //const [absentReason, setAbsentReason] = useState({});
    
    const [switchEmployeeFunction, setSwitchEmployeeFunction] = useState(false)

    const [employees, setEmployees] = useState([]);
    const [employeesSchedulingTime, setEmployeesSchedulingTime] = useState([]);
    const [employeesCoreWorkTime, setEmployeesCoreWorkTime] = useState([]);
    const [targetHourTime, setTargetHourTime] = useState([]);
    const [absentState, setAbsentState] = useState({});
    const [getAllTimePolicy, setGetAllTimePolicy] = useState([]);

    const [selectedArea, setSelectedArea] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const monday = currentDate;
                const sunday = new Date(monday);
                sunday.setDate(monday.getDate() + 6);
                const start = monday.toISOString().slice(0, 10);
                const end = sunday.toISOString().slice(0, 10);

                const [employeesResult, schedulingResult, coreTimeResult, pausePolicyResult] = await Promise.all([
                    axios.get('employee/getActive'),
                    axios.get(`scheduling/getAllInDuration?start=${start}&end=${end}`),
                    axios.get('coreWorkingHoursPolicy/getAll'),
                    axios.get("minimumPauseTimePolicy/getAll")
                ]);

                // Process all data
                setEmployees(employeesResult.data);
                setEmployeesSchedulingTime(schedulingResult.data);
                setEmployeesCoreWorkTime(coreTimeResult.data);
                
                // Compact pause policy processing
                const pausePolicy = pausePolicyResult.data[0];
                const tempArray = Array.from({length: 16}, (_, i) => 
                    pausePolicy[`above_${i}_hours_constraint_in_minutes`]
                );
                setGetAllTimePolicy(tempArray);

                // Process absent state
                const newAbsentState = {}; 
                schedulingResult.data.forEach((object) => {                    
                    if(object.absent){
                        const dateLocalString = new Date(object.date).toLocaleDateString('de-DE', DATE_OPTIONS)
                        
                        // 2. Populate the new object
                        newAbsentState[`${object.employee_id}-${dateLocalString}`] = {
                                absent: object.absent,
                                comment: object.comment
                            }
                    }
                });
                // 3. Set the state only ONCE
                setAbsentState(newAbsentState);

                // Fetch target hours
                const targetHourPromises = employeesResult.data.map(employee => 
                    axios.get(`workingTimeAccount/getAllForEmployeeWithId?id=${employee.id}`)
                );
                const targetHourResults = await Promise.all(targetHourPromises);
                const targetHourArray = targetHourResults.map(result => result.data[0]).filter(Boolean);
                
                setTargetHourTime(targetHourArray);
                setIsLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                Logout();
            }
        }    
        fetchData();    
                    
    }, [currentDate, isLoading])

    const handleSaveChanges = () => {
        const requests = Object.values(localChanges).map(change => {
            let dateArray = change.date.split(".");
            let date = dateArray[2] + "-" + dateArray[1] + "-" + dateArray[0];
            if (change.toDelete && change.id) {
                return axios.delete(`scheduling/delete?id=${change.id}`);
            } else if (change.id) {
                return axios.put('scheduling/update', {
                    id: change.id,
                    date: date,
                    start_time: change.start_time,
                    end_time: change.end_time,
                    strict_mode: true,
                    comment: change.comment, 
                    absent: change.absent
                });
            } else if (!change.toDelete) {                
                return axios.post('scheduling/create', {
                    employee_id: change.employee_id,
                    date: date,
                    start_time: change.start_time,
                    end_time: change.end_time,
                    strict_mode: true,
                    comment: change.comment,
                    absent: change.absent,
                });
            }
            return null;
        }).filter(request => request !== null);

        Promise.all(requests).then(() => {
            sessionStorage.setItem("currentDate", currentDate);
            setLocalChanges({});
            setIsLoading(true);
            setSnackbarConfig({ open: true, message: "Änderungen erfolgreich gespeichert.", variant: "success" });
        }).catch(() => {
            setSnackbarConfig({ open: true, message: "Fehler beim Speichern der Änderungen.", variant: "error" });
        });
    };

    function getMonday(date) {
        date = new Date(date);
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(date.setDate(diff));
    }

    function getWeekSunday(date) {
        date = new Date(date);
        const day = date.getDay();
        const diff = date.getDate() + (day === 0 ? 0 : 7 - day);
        return new Date(date.setDate(diff));
    }


    const handleNextWeek = () => {
        let newWeekDate = getMonday(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 7))
        newWeekDate.setHours(10, 0, 0, 0);
        setCurrentDate(newWeekDate);
        setIsLoading(true);
    }

    const handleLastWeek = () => {
        let newWeekDate = getMonday(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 7))
        newWeekDate.setHours(10, 0, 0, 0);
        setCurrentDate(newWeekDate);
        setIsLoading(true);
    }

    function getWeekButtons() {
        return (
            <>
                <IconButton aria-label="next" disabled={Object.keys(localChanges).length > 0} onClick={handleLastWeek}>
                    <ChevronLeftIcon />
                </IconButton>
                <p>KW {GetWeekNumber(currentDate)} {getWeekSunday(currentDate).getFullYear()}</p>
                <IconButton aria-label="next" disabled={Object.keys(localChanges).length > 0} onClick={handleNextWeek}>
                    <ChevronRightIcon />
                </IconButton>
            </>
        )
    }

    const handleLastDay = () => {
        const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 1);
        lastDay.setHours(0, 0, 0, 0);
        setCurrentDate(lastDay);
    }

    const handleNextDay = () => {
        const nextDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1);
        nextDay.setHours(0, 0, 0, 0);
        setCurrentDate(nextDay);
    }

    function getDaysButtons() {
        return (
            <>
                <IconButton aria-label="next" disabled={Object.keys(localChanges).length > 0} onClick={handleLastDay}>
                    <ChevronLeftIcon />
                </IconButton>
                <p>{currentDate.toLocaleDateString("de-DE", { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                <IconButton aria-label="next" disabled={Object.keys(localChanges).length > 0} onClick={handleNextDay}>
                    <ChevronRightIcon />
                </IconButton>
            </>
        )
    }

    const handleTodayButtonClicked = () => {
        if(!enableTimeline){
            setCurrentDate(getMonday(new Date()));
            setIsLoading(true); 
        } else {
            const newToday = new Date();
            newToday.setHours(0,0,0,0);
            setCurrentDate(newToday);
            setIsLoading(true);
        }
    }

    const handleTimelineSwitch = (event) => {
        const checked = event.target.checked;
        setEnableTimeline(checked);

        setIsLoading(true);
        
        if (checked){
            const newCurrentDate = currentDate;
            newCurrentDate.setHours(0,0,0,0);
            setCurrentDate(newCurrentDate);
        } else {
            const newCurrentDate = getMonday(currentDate);
            newCurrentDate.setHours(10,0,0,0);
            setCurrentDate(newCurrentDate);
        }
    }

    const handleDeleteLocalChanges = () => {
        setLocalChanges({});
        setIsLoading(true);
    }

    return (
        <div className="work-time-planning-page">
            <div className="work-time-planning-header">
                <h1>Arbeitszeitplanung</h1>
                <div className="work-time-planning-today-button">
                    <PrimaryButton text="Heute" onClick={handleTodayButtonClicked}/>
                </div>
                <div className="work-time-planning-next-back-buttons">
                    {enableTimeline ? getDaysButtons() : getWeekButtons()}
                </div>
                <CancelButton text="Wochenplan herunterladen" onClick={() => window.print()}/>
                <FormControlLabel
                    control={<Switch checked={enableTimeline} onChange={(event)=> handleTimelineSwitch(event)}/>}
                    label="Zeitplan anzeigen"
                />
            </div>
            <h3 className="not-visible-info red">Planung kann nur auf einem größeren Bildschirm stattfinden.</h3>
            <div className="planning-information-section">
                <p>Hier können alle Arbeitszeiten der Mitarbeitenden geplant werden. Die <span className="bold">Kernarbeitszeiten</span> werden <span className="bold">automatisch</span> eingefügt. Diese können hier für die geplanten Tage <span className="bold">überschrieben</span> werden.</p>
                <p><span className="red-color">Achtung:</span> Mitarbeitende können sich nur in den geplanten Zeiten einloggen.</p>
            </div>
            <div className="work-time-planning-save-button">
                <PrimaryButton text="Speichern" onClick={handleSaveChanges} disabled={Object.keys(localChanges).length === 0}/>
                <CancelButton text="Änderungen löschen" onClick={handleDeleteLocalChanges} disabled={Object.keys(localChanges).length === 0} />
                {!enableTimeline && <FormControlLabel
                    control={<Switch checked={switchEmployeeFunction} onChange={(event)=> setSwitchEmployeeFunction(event.target.checked)}/>}
                    label="Mitarbeiterfunktion einblenden"
                />}
                <FilterArea selectedArea={selectedArea} setSelectedArea={setSelectedArea} />
            </div>
            {!enableTimeline && 
                <PlanningTable 
                    currentDate={currentDate}
                    isLoading={isLoading}
                    localChanges={localChanges}
                    setLocalChanges={setLocalChanges}
                    switchEmployeeFunction={switchEmployeeFunction}

                    employees={employees}
                    employeesSchedulingTime={employeesSchedulingTime}
                    employeesCoreWorkTime={employeesCoreWorkTime}
                    targetHourTime={targetHourTime}
                    absentState={absentState}
                    setAbsentState={setAbsentState}
                    getAllTimePolicy={getAllTimePolicy}

                    selectedArea={selectedArea}
                />
            }
            <CustomSnackbar 
                variant={snackbarConfig.variant} 
                message={snackbarConfig.message} 
                open={snackbarConfig.open} 
                onClose={() => setSnackbarConfig({ ...snackbarConfig, open: false })} 
            />
            {enableTimeline && 
                <PlanningScheduler 
                    currentDate={currentDate} 
                    setLocalChanges={setLocalChanges}
                    isLoading={isLoading}

                    employees={employees}
                    employeesSchedulingTime={employeesSchedulingTime}
                    employeesCoreWorkTime={employeesCoreWorkTime}
                    targetHourTime={targetHourTime}
                    absentState={absentState}
                    getAllTimePolicy={getAllTimePolicy}

                    selectedArea={selectedArea}
                />
            }
        </div>
    )
}

export default WorkTimePlanning;